/**
 * Il piano START, lato web.
 *
 * [sc-48497 / task 48524 e 48526] Il file ha lo stesso nome del suo gemello in
 * `crmstart-api/src/config/`, come `features.js`, `catalog.js` e `branding.js`:
 * i numeri che le due parti devono dire uguali stanno allo stesso indirizzo da
 * tutt'e due i lati. Qui servono a scriverli nelle schermate senza cablarli
 * dentro un testo — «10 giorni» compare in tre punti, e devono cambiare tutti
 * insieme.
 *
 * Il rispetto delle regole resta al server: questi valori raccontano, non
 * decidono.
 */

/**
 * Quanto entra nel piano START.
 *
 * [sc-48497 / task 48529] Gemello di `crmstart-api/src/config/plans.js`: qui i
 * numeri si raccontano al cliente, di la' si fanno rispettare. Se divergessero,
 * leggerebbe un tetto e ne troverebbe un altro.
 */
export const PLAN_START = Object.freeze({
  name: "START",
  maxOperators: 2,
  maxItems: 10000,
});

/**
 * Il piano PLUS: niente tetti.
 *
 * [sc-48497 / task 48527] Qui prima c'era solo il nome, di proposito: tetti
 * inventati sarebbero stati peggio di nessun tetto. [sc-48351] Il documento
 * «modello crm start/plus» li ha decisi: utenti e articoli «potenzialmente
 * illimitati». `null` vuol dire proprio questo, come nel gemello dell'API.
 * Quali funzioni accende non si ridice qui: arriva dall'API col profilo.
 */
export const PLAN_PLUS = Object.freeze({
  name: "PLUS",
  maxOperators: null,
  maxItems: null,
});

/** Giorni di prova del piano START. */
export const TRIAL_DAYS = 10;

/**
 * Quando crmstart avvisa che la prova sta finendo: giorni che mancano.
 *
 * [sc-48497 / task 48528] Gemello dell'elenco lato API, che manda le email.
 * Qui serve al banner, che sotto la seconda soglia cambia tono: se i due
 * elenchi divergessero, il cliente riceverebbe l'avviso mentre a video legge
 * ancora che c'e' tempo.
 */
export const TRIAL_NOTICE_DAYS = Object.freeze([5, 2, 0]);

/** Sotto questa soglia la prova e' agli sgoccioli: e' il secondo preavviso. */
export const TRIAL_URGENT_DAYS = 2;

/**
 * Quanto restano i dati dopo la fine della prova, prima di sparire.
 *
 * [sc-48497 / task 48532] Gemello del numero lato API, che cancella davvero.
 * Qui non decide niente - la data della cancellazione arriva gia' calcolata
 * col profilo - ma serve a scriverla nelle pagine che spiegano cosa succede
 * dopo la scadenza, senza inventarsi un secondo numero.
 */
export const RETENTION_DAYS = 90;

/** Quanto vale il collegamento di conferma dell'email. */
export const VERIFICATION_TTL_HOURS = 48;

/** Lunghezza minima della password scelta da chi si registra. */
export const MIN_PASSWORD_LENGTH = 10;

/** Stati possibili di un'azienda, come nell'ENUM `companies.status`. */
export const COMPANY_STATUS = Object.freeze({
  TRIAL: "prova",
  ACTIVE: "attivo",
  EXPIRED: "scaduto",
});

/**
 * Cosa puo' fare l'azienda, adesso. Gemella della funzione omonima lato API.
 *
 * [sc-48497 / task 48527] Il server decide davvero: rifiuta le scritture con
 * `TRIAL_EXPIRED`. Questa serve al banner e a non far entrare il cliente in
 * una schermata di creazione che poi si rifiuterebbe di salvare.
 *
 * @param {{status?: string|null, trialEndsAt?: string|null, daysLeft?: number|null, readOnly?: boolean}} company
 * @returns {{status: string|null, daysLeft: number|null, readOnly: boolean}}
 */
export function trialState(company) {
  return {
    status: company?.status ?? null,
    daysLeft: typeof company?.daysLeft === "number" ? company.daysLeft : null,
    readOnly: Boolean(company?.readOnly),
    // [sc-48497 / task 48532] Quando spariscono i dati. La calcola l'API e
    // vale solo a prova finita: qui si copia e basta, perche' un conto rifatto
    // sull'orologio del browser potrebbe dire un giorno diverso da quello
    // scritto nell'email.
    deletionAt: company?.deletionAt ?? null,
  };
}
